import { GeoJsonLayer } from '@deck.gl/layers'
import DeckMap from './DeckMap'
import mapsApiClient from './mapsApiClient'
import { Matrix4, Vector3 } from '@math.gl/core'
import CustomMVTLayer from './CustomMVTLayer'

export const tooltipStyle = {
  background: 'var(--colorWhite)',
  color: 'var(--colorHigh)',
  margin: '8px',
  'border-radius': '4px',
  'box-shadow': '0 0 4px 1px rgba(0,0,0, 0.2)'
}

const WORLD_SIZE = 512

const DEFAULT_MVT_PROPS = {
  pointRadiusMinPixels: 2,
  lineWidthMinPixels: 1,
  getFillColor: [130, 109, 186, 255],
  getLineColor: [44, 44, 44]
}

export default {
  layers: {},
  deckInstance: null,
  init (deckProps) {
    this.deckInstance = new DeckMap(deckProps)
  },
  destroy () {
    this.deckInstance.finalize()
    delete this.deckInstance
  },
  hasLayer (id) {
    return !!this.layers[id]
  },
  isVisible (id) {
    // visible = undefined is treated by deckgl as visible = true
    return this.layers[id] && this.layers[id].visible !== false
  },
  async fetchMVTTemplate (sql, options) {
    const data = await mapsApiClient.createMap(sql, options)
    const stats = data.metadata.layers[0].meta.stats
    const urlData = data.metadata.url.vector
    let urls = [urlData.urlTemplate]
    if (urlData.subdomains.length > 0) {
      urls = urlData.subdomains.map(s => urlData.urlTemplate.replace('{s}', s))
    }
    return { urls, stats }
  },
  async addSQLLayer ({ id, sql, sublayer: SubLayer = GeoJsonLayer, requestOptions = {}, ...props }) {
    if (!sql) {
      throw new Error(`[layerService.addSQLLayer] sql must be defined. Received ${sql} instead`)
    }
    const { urls, stats } = await this.fetchMVTTemplate(sql, requestOptions)
    this.addLayer({
      id,
      layerType: CustomMVTLayer,
      data: urls,
      stats,
      requestOptions,
      ...DEFAULT_MVT_PROPS,
      ...props,
      renderSubLayers: props.renderSubLayers || (innerprops => new SubLayer({
        ...innerprops,
        extensions: props.disableClipping
          ? []
          : innerprops.extensions
      }))
    })
  },
  async updateSQLLayer ({ id, sql }) {
    if (!this.layers[id]) {
      return
    }
    const requestOptions = this.layers[id].requestOptions
    const { urls, stats } = await this.fetchMVTTemplate(sql, requestOptions)
    this.updateLayer(id, {
      data: urls,
      stats
    })
  },
  updateDeckInstance () {
    if (!this.deckInstance) {
      return
    }
    const layers = Object.values(this.layers)
      .sort((l1, l2) => ((l1.zIndex || 0) - (l2.zIndex || 0)))
      // NOTE: uncomment next line if you are rendering many (>= 5) MVTLayers at the same time
      // .filter(l => this.isVisible(l.id))
      .map(({ layerType: LayerClass, ...props }) => new LayerClass(props))
    if (this.deckInstance) {
      this.deckInstance.setProps({ layers })
    }
  },
  addLayer (layer) {
    if (!layer.id) {
      throw new Error(`[layerService.addLayer] layer id must defined. Received "${layer.id}" instead`)
    }
    if (typeof layer.layerType !== 'function') {
      throw new Error(`[layerService.addLayer] layerType must be a function. Received "${layer.layerType}" instead`)
    }
    this.layers[layer.id] = layer
    this.updateDeckInstance()
  },
  updateLayer (id, data) {
    if (!this.layers[id]) {
      return
    }
    this.layers[id] = {
      ...this.layers[id],
      ...data
    }
    this.updateDeckInstance()
  },
  removeLayer (id) {
    if (this.hasLayer(id)) {
      delete this.layers[id]
      this.updateDeckInstance()
    }
  },
  hideLayer (id) {
    if (this.hasLayer(id)) {
      this.updateLayer(id, { visible: false })
    }
  },
  showLayer (id) {
    if (this.hasLayer(id)) {
      this.updateLayer(id, { visible: true })
    }
  },
  getZoomFactor () {
    const viewport = this.deckInstance.getViewports()[0]
    return viewport.metersPerPixel
  },
  getFrustumPlanes () {
    const viewports = this.deckInstance.getViewports(undefined)
    return viewports[0].getFrustumPlanes()
  },
  getTransformationMatrixFromTile ({ x, y, z }) {
    const worldScale = 2 ** z

    const xScale = WORLD_SIZE / worldScale
    const yScale = -xScale

    const xOffset = (WORLD_SIZE * x) / worldScale
    const yOffset = WORLD_SIZE * (1 - y / worldScale)

    return new Matrix4()
      .translate([xOffset, yOffset, 0])
      .scale([xScale, yScale, 1])
  },
  transformPoint (point, matrix) {
    return matrix.transform(point)
  },
  isPointInViewport (point, planes) {
    const position = new Vector3(point[0], point[1], 0)
    let outDir = null
    for (const direction in planes) {
      const plane = planes[direction]
      if (position.dot(plane.normal) > plane.distance) {
        outDir = direction
        break
      }
    }

    return outDir === null
  },
  isFeatureInViewport (feature, cache, matrix, planes) {
    if (!feature.properties) {
      return false
    }
    // TODO: make this configurable
    const id = feature.properties.cartodb_id || feature.id
    if (cache.has(id)) {
      return false // avoid duplicate features across tiles
    }

    const point = this.transformPoint(feature.geometry.coordinates, matrix)
    const isInside = this.isPointInViewport(point, planes)

    if (isInside) {
      cache.add(id)
    }

    return isInside
  },
  // code adapted from here: https://github.com/CartoDB/toolkit/blob/jb/viewport-features-generator/packages/viz/src/lib/interactivity/viewport-features/ViewportFeaturesGenerator.ts
  // TODO: this only supports points now
  getViewportFeatures (layerId) {
    if (!this.deckInstance) {
      return []
    }

    const layer = this.deckInstance.layerManager.layers.find(l => l.id === layerId)
    if (!layer) {
      return []
    }

    const cache = new Set()
    const planes = this.getFrustumPlanes()
    const tiles = layer.state.tileset.selectedTiles

    return tiles.map(t => {
      const matrix = this.getTransformationMatrixFromTile(t)
      const features = t.content || []
      return features.filter(f => this.isFeatureInViewport(f, cache, matrix, planes))
    }).flat()
  }
}
